import { useState, useEffect } from 'react'
import { Layout, Menu, Button, Popconfirm, Avatar, Dropdown, Space } from 'antd'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  DashboardOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  LogoutOutlined,
  UserOutlined,
  GiftOutlined,
  BarChartOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  CloudServerOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { clearAuth, getUserInfo, getTokenRemainingTime } from '../../utils/auth'
import { authAPI } from '../../api'
import { recordOperation } from '../../utils/operationLog'
import './index.css'

const { Header, Sider, Content } = Layout

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [userInfo, setUserInfo] = useState<Record<string, any> | null>(null)
  const [remainingTime, setRemainingTime] = useState('')
  const navigate = useNavigate()
  const location = useLocation()

  // 获取用户信息
  useEffect(() => {
    const info = getUserInfo()
    setUserInfo(info)
  }, [])

  // 定期更新 token 剩余时间显示
  useEffect(() => {
    const updateRemainingTime = () => {
      const ms = getTokenRemainingTime()
      if (ms <= 0) {
        setRemainingTime('即将过期')
        return
      }
      const hours = Math.floor(ms / (1000 * 60 * 60))
      const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
      if (hours > 0) {
        setRemainingTime(`${hours}小时${minutes}分`)
      } else {
        setRemainingTime(`${minutes}分钟`)
      }
    }

    updateRemainingTime()
    const timer = setInterval(updateRemainingTime, 60000) // 每分钟更新
    return () => clearInterval(timer)
  }, [])

  const menuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
    { key: '/products', icon: <ShoppingOutlined />, label: '商品管理' },
    { key: '/products/price', icon: <ShoppingOutlined />, label: '价格日期' },
    { key: '/products/certs', icon: <SafetyCertificateOutlined />, label: '商品证件' },
    { key: '/orders', icon: <ShoppingCartOutlined />, label: '订单管理' },
    { key: '/users', icon: <UserOutlined />, label: '用户管理' },
    { key: '/coupons', icon: <GiftOutlined />, label: '优惠券管理' },
    { key: '/statistics', icon: <BarChartOutlined />, label: '数据报表' },
    { key: '/settings/home', icon: <SettingOutlined />, label: '首页配置' },
    { key: '/settings/delivery', icon: <SettingOutlined />, label: '开通区域与运费' },
    { key: '/settings/admin', icon: <SettingOutlined />, label: '管理员与角色' },
    { key: '/settings/certs', icon: <SafetyCertificateOutlined />, label: '公司证件管理' },
    { key: '/settings/logs', icon: <HistoryOutlined />, label: '操作日志' },
    { key: '/settings/backup', icon: <CloudServerOutlined />, label: '数据备份' },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  const handleLogout = async () => {
    try {
      // 尝试调用后端登出接口
      await authAPI.logout()
    } catch (e) {
      // 忽略登出接口错误，仍然清除本地状态
    }

    // 记录登出日志（在清除 token 之前）
    recordOperation('认证管理', '登出', userInfo?.username || '')

    // 清除本地认证信息
    clearAuth()

    // 跳转到登录页
    window.location.replace('/login')
  }

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'user',
      label: (
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontWeight: 500 }}>{userInfo?.username || '管理员'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>
            Token 剩余：{remainingTime}
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      label: (
        <span>
          <LogoutOutlined style={{ marginRight: 8 }} />
          退出登录
        </span>
      ),
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
        <div className="logo">伴成商城</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 500 }}>管理后台</div>
          <div className="header-right">
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: ({ key }) => {
                  if (key === 'logout') {
                    handleLogout()
                  }
                },
              }}
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size="small" icon={<UserOutlined />} src={userInfo?.avatar} />
                <span>{userInfo?.username || '管理员'}</span>
                <DownOutlined style={{ fontSize: 12 }} />
              </Space>
            </Dropdown>
            <Popconfirm
              title="确定退出登录吗？"
              onConfirm={handleLogout}
              okText="退出"
              cancelText="取消"
            >
              <Button type="link" icon={<LogoutOutlined />}>
                退出登录
              </Button>
            </Popconfirm>
          </div>
        </Header>
        <Content style={{ margin: '16px' }}>
          <div style={{ padding: 24, background: '#fff', minHeight: 360 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
